import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DayPicker, type DateRange } from 'react-day-picker'
import { Clock, CalendarDays } from 'lucide-react'
import { Select } from '../../components/ui/Select'
import { it as itLoc, enGB, de as deLoc } from 'date-fns/locale'
import { differenceInCalendarDays } from 'date-fns'
import { getMainContext } from '../../lib/api/booking'
import { useBooking } from '../../store/bookingStore'
import { StepShell } from '../../components/layout/StepShell'
import { Skeleton, ErrorState } from '../../components/ui/States'
import { buildTimeSlots, hhmm, parseDay, toDay } from '../../lib/format'
import { stepPath } from '../../lib/steps'
import type { Locale, TransportContext } from '../../lib/api/types'
import { TransportIcon } from '../../components/ui/TransportIcon'
import { cn } from '../../lib/cn'

const DF = { it: itLoc, en: enGB, de: deLoc }

export function DatesStep() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const navigate = useNavigate()
  const { locale: routeLocale = 'it' } = useParams()

  const cart = useBooking((s) => s.cart)
  const setContexts = useBooking((s) => s.setContexts)
  const setTransport = useBooking((s) => s.setTransport)
  const setDates = useBooking((s) => s.setDates)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['mainContext'],
    queryFn: getMainContext,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (data) setContexts(data)
  }, [data, setContexts])

  const [transportId, setTransportId] = useState<number | null>(cart.transportId)
  const [range, setRange] = useState<DateRange | undefined>(
    cart.fromDay ? { from: parseDay(cart.fromDay), to: cart.toDay ? parseDay(cart.toDay) : undefined } : undefined,
  )
  const [time, setTime] = useState<string>(cart.fromHour ?? '')

  // default-select the first transport once data arrives
  useEffect(() => {
    if (data?.length && transportId == null) setTransportId(data[0].transport.id)
  }, [data, transportId])

  const ctx: TransportContext | undefined = useMemo(
    () => data?.find((c) => c.transport.id === transportId),
    [data, transportId],
  )

  // default time to the transport's earliest allowed slot
  useEffect(() => {
    if (ctx && !time) setTime(ctx.minTime)
  }, [ctx, time])

  const timeSlots = useMemo(() => {
    if (!ctx) return []
    const onMinDay = range?.from && toDay(range.from) === ctx.minDate
    return buildTimeSlots(ctx.bookingHoursSubdivision, onMinDay ? ctx.minTime : '00:00:00')
  }, [ctx, range?.from])

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0
  const overMax = ctx ? nights > ctx.bookingMaxDays : false
  const canContinue = Boolean(transportId && range?.from && range?.to && time && !overMax)

  function onContinue() {
    if (!canContinue || !range?.from || !range?.to || !transportId) return
    if (transportId !== cart.transportId) setTransport(transportId)
    setDates(toDay(range.from), time, toDay(range.to))
    navigate(stepPath(routeLocale, 'rate'))
  }

  return (
    <StepShell
      stepKey="dates"
      title={t('dates.title')}
      subtitle={t('dates.subtitle')}
      showPrice={false}
      primaryLabel={t('dates.checkAvailability')}
      onPrimary={onContinue}
      primaryDisabled={!canContinue}
    >
      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : (
        // desktop: vehicle+calendar (left) and time+summary (right, sticky); mobile: stacked in DOM order
        <div className="space-y-6 lg:mx-auto lg:grid lg:max-w-3xl lg:grid-cols-[1fr_15rem] lg:items-start lg:gap-7 lg:space-y-0">
          <div className="space-y-6">
          {/* transport selector */}
          <section>
            <p className="gsm-label">{t('dates.vehicle')}</p>
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              {isLoading
                ? [0, 1].map((i) => <Skeleton key={i} className="h-20" />)
                : data?.map((c) => {
                    const active = c.transport.id === transportId
                    return (
                      <button
                        key={c.transport.id}
                        onClick={() => setTransportId(c.transport.id)}
                        className={cn(
                          'group flex items-center gap-3 rounded-2xl border-2 bg-white p-4 text-left transition-all',
                          active
                            ? 'border-brand-600 shadow-card-hover'
                            : 'border-ink-100 hover:border-ink-200 hover:shadow-card',
                        )}
                      >
                        <span className="flex h-14 w-16 flex-none items-center justify-center">
                          <TransportIcon iconUri={c.transport.iconUri} label={c.transport.label} />
                        </span>
                        <span className="font-display font-bold text-ink-900">{c.transport.label}</span>
                      </button>
                    )
                  })}
            </div>
          </section>

          {/* calendar (fills the container width) */}
          <section>
            <div className="gsm-card p-3 sm:p-4">
              <div className="mb-2 flex items-center gap-2 px-1 text-sm font-semibold text-ink-700">
                <CalendarDays className="h-4 w-4 text-brand-600" /> {t('dates.selectRange')}
              </div>
              {isLoading || !ctx ? (
                <Skeleton className="h-72 w-full" />
              ) : (
                <DayPicker
                  className="w-full"
                  mode="range"
                  locale={DF[locale]}
                  numberOfMonths={1}
                  weekStartsOn={1}
                  selected={range}
                  onSelect={setRange}
                  startMonth={parseDay(ctx.minDate)}
                  disabled={[{ before: parseDay(ctx.minDate) }, { after: parseDay(ctx.maxDate) }]}
                />
              )}
            </div>
          </section>
          </div>

          {/* time + nights → desktop sidebar (sticky) */}
          <section className="space-y-2 lg:sticky lg:top-24">
            <label className="gsm-label flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {t('dates.time')}
            </label>
            <Select
              ariaLabel={t('dates.time')}
              value={time}
              options={timeSlots.map((s) => ({ value: s, label: hhmm(s) }))}
              onChange={setTime}
              icon={<Clock className="h-4 w-4 text-brand-600" />}
              openUp
              disabled={!ctx}
            />
            {nights > 0 && (
              <p className={cn('text-sm', overMax ? 'text-brand-600' : 'text-ink-400')}>
                {nights} {nights === 1 ? t('common.day') : t('common.days')}
                {overMax && ctx ? ` · max ${ctx.bookingMaxDays}` : ''}
              </p>
            )}

            {/* selected period — compact, shown under the time */}
            <div className="grid grid-cols-2 gap-2 pt-1 lg:grid-cols-1">
              <CompactDate label={t('dates.arrival')} value={range?.from ? formatLong(range.from, locale) : '—'} active={Boolean(range?.from)} />
              <CompactDate label={t('dates.departure')} value={range?.to ? formatLong(range.to, locale) : '—'} active={Boolean(range?.to)} />
            </div>
          </section>
        </div>
      )}
    </StepShell>
  )
}

function CompactDate({ label, value, active }: { label: string; value: string; active?: boolean }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-2 rounded-lg bg-white px-3 py-1.5 ring-1 transition', active ? 'ring-brand-200' : 'ring-ink-100')}>
      <span className="text-[11px] font-medium text-ink-400">{label}</span>
      <span className={cn('truncate text-[12.5px] font-semibold capitalize', active ? 'text-ink-900' : 'text-ink-300')}>
        {value}
      </span>
    </div>
  )
}

function formatLong(d: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === 'it' ? 'it-IT' : locale === 'de' ? 'de-DE' : 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d)
}
